/**
 * Marketing Automation Dictionary
 *
 * Usage:
 *  Maps an external (Autopilot) field to an internal field; create
 *  a key of the path in the root Vuex state object.
 *
 *  Object fields and their meaning:
 *    - fieldName: string  the name of the field in Autopilot
 *    - type:      string  the type of the field in Autopilot. Refer to API for type definitions
 *                         if the type already exists.
 *    - custom:    boolean if the field is a custom field, this field must be set to `true`
 *    - required:  boolean if this value is not set, or false, no update will be sent to the automation service
 */
import { cleanValue } from '../../utilities/Currency';
import { isNonEmptyString } from '../../utilities/isNonEmptyString';

export default {
  'global.reasonForLoanChange': {
    fieldName: 'Intent',
    type: 'string',
    custom: true
  },

  'global.submissionTime': {
    fieldName: 'SuccessfullySubmitted',
    type: 'boolean',
    custom: true
  },

  'global.appId': {
    fieldName: 'AppID',
    type: 'string',
    custom: true
  },

  'applicant.primary.firstName': {
    fieldName: 'FirstName',
    type: 'string',
    custom: false
  },

  'applicant.primary.lastName': {
    fieldName: 'LastName',
    type: 'string',
    custom: false
  },

  'applicant.primary.emailAddress': {
    fieldName: 'Email',
    type: 'string',
    custom: false,
    required: true
  },

  'applicant.primary.dateOfBirth': {
    fieldName: 'BirthDate',
    type: 'date',
    custom: true,
    mutate (value) {
      // for this to work correctly we need
      // to replace - with / in the date string
      const dateString = isNonEmptyString(value)
        ? value.replace(/-/g, '/')
        : value;

      return new Date(dateString + ' 12:00 pm').toUTCString();
    }
  },

  'applicant.primary.mobileNumber': {
    fieldName: 'MobilePhone',
    type: 'string',
    custom: true
  },

  'residence.primary.current.address.street': {
    fieldName: 'MailingStreet',
    type: 'string',
    custom: false
  },

  'residence.primary.current.address.city': {
    fieldName: 'MailingCity',
    type: 'string',
    custom: false
  },

  'residence.primary.current.address.state': {
    fieldName: 'MailingState',
    type: 'string',
    custom: false
  },

  'residence.primary.current.address.zipCode': {
    fieldName: 'MailingPostalCode',
    type: 'string',
    custom: false
  },

  'employment.primary.current.grossAnnualIncome': {
    fieldName: 'Income',
    type: 'string',
    custom: true,
    mutate (value) {
      return cleanValue(value);
    }
  },

  'trackingData.sponsorId': {
    fieldName: 'SourceID',
    type: 'string',
    custom: true
  },

  'trackingData.sponsorClickId': {
    fieldName: 'SubId',
    type: 'string',
    custom: true
  },

  'vehicle.mileage': {
    fieldName: 'Mileage',
    type: 'integer',
    custom: true,
    mutate (value) {
      return value ? value.replace(/,/g, '') : 0;
    }
  },

  'vehicle.year': {
    fieldName: 'VehicleYear',
    type: 'string',
    custom: true
  },

  'vehicle.make.name': {
    fieldName: 'VehicleMake',
    type: 'string',
    custom: true
  },

  'vehicle.model.name': {
    fieldName: 'VehicleModel',
    type: 'string',
    custom: true
  },

  'vehicle.trim.name': {
    fieldName: 'VehicleTrim',
    type: 'string',
    custom: true
  }
};
